import React, { useEffect, useState } from 'react';
import { Filter, JobPortalScreen, JobResult, SearchParams } from 'jobs.types';
import { useTranslation } from 'react-i18next';
import { PagesectionSubsection, SearchBar } from '@ktag/styleguide/dist/react';
import Abo from 'components/abo/Abo';
import AdminView from 'components/adminview/AdminView';
import useFetchData from 'api/useFetchData';
import filterJobs from 'javascripts/filterJobs';
import LoadMore from 'components/loadmore/LoadMore';
import JobsHeader from 'components/jobsheader/JobsHeader';
import JobList from '../joblist/JobList';
import JobFilters from '../jobfilters/JobFilters';
import Topics from '../topics/Topics';
import JobportalResultsHeader from './JobportalResultsHeader';
import { INITIAL_PAGE_SIZE, JOBS_MAX_LIMIT } from '../../constants';

interface Props {
  mocked?: boolean;
  api: string;
  title?: string;
  moreLink?: string;
  maxEntries?: number;
  newThresholdHours: string;
  preFilter?: { [key: string]: string[] };
  filterJobIds?: string[];
  listOnly?: boolean;
  careerUrl?: string;
  volksschuleUrl?: string;
  apprenticeshipUrl?: string;
  privacyUrl?: string;
  loginUrl?: string;
}

const Jobportal = ({
  mocked,
  api,
  title,
  moreLink,
  maxEntries,
  newThresholdHours,
  preFilter,
  filterJobIds,
  listOnly,
  careerUrl,
  volksschuleUrl,
  apprenticeshipUrl,
  privacyUrl,
  loginUrl,
}: Props) => {
  const { t } = useTranslation('jobs');
  const initialFilter: {
    search: SearchParams;
    jobAbo: SearchParams;
  } = {
    search: {
      sort: '-startDate',
      limit: JOBS_MAX_LIMIT,
      q: '',
      ...(preFilter || {}),
    },
    jobAbo: {
      ...(preFilter || {}),
    },
  };

  if (maxEntries) {
    initialFilter.search.limit = maxEntries;
  }

  const [filter, setFilter] = useState(initialFilter);
  const [showNResults, setShowNResults] = useState(maxEntries || INITIAL_PAGE_SIZE);
  const [showMoreResultsButton, setShowMoreResultsButton] = useState(false);
  const [screen, setScreen] = useState<JobPortalScreen>('jobsearch');
  const [filteredJobs, setFilteredJobs] = useState<JobResult[]>([]);

  const { attributes, jobs, error, loading } = useFetchData(api, mocked);

  const updateFilters = (newFilters: Filter) => {
    filterJobs(jobs, attributes, newFilters, setFilteredJobs, filterJobIds);
    setFilter(newFilters);
  };

  useEffect(() => {
    if (!jobs?.length) return;

    updateFilters(filter);
  }, [loading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [screen]);

  useEffect(() => {
    if (maxEntries) return;

    if (filteredJobs?.length > showNResults) {
      setShowMoreResultsButton(true);
    } else {
      setShowMoreResultsButton(false);
    }
  }, [filteredJobs?.length, showNResults]);

  useEffect(() => {
    updateFilters(filter);
  }, [showNResults]);

  const onSearch = (search: string) => {
    const newFilter = { ...filter };
    newFilter.search.q = search;
    newFilter.jobAbo.q = search;
    updateFilters(newFilter);
  };

  const showMoreResults = () => {
    setShowNResults(200);
  };

  const slicedJobs = filteredJobs?.slice(0, showNResults);

  if (listOnly)
    return (
      <>
        <JobsHeader title={title} moreLink={moreLink} />
        {filteredJobs?.length === 0 && !loading && (
          <h2 className="jobportal__noresult-header">{t('jobportal_noresults')}</h2>
        )}
        <JobList
          jobs={slicedJobs}
          loading={loading}
          newThresholdHours={newThresholdHours}
          listOnly
        />
        <LoadMore hasMore={showMoreResultsButton} onLoadMore={showMoreResults} />
      </>
    );

  if (screen === 'jobabo') {
    return (
      <Abo
        setScreen={setScreen}
        filter={filter}
        attributes={attributes}
        setFilter={updateFilters}
        api={api}
        mocked={mocked}
        privacyUrl={privacyUrl}
      />
    );
  }

  return (
    <section className="jobportal">
      <AdminView attributes={attributes} jobs={jobs} />
      <PagesectionSubsection pull={1}>
        <SearchBar
          placeholder={t('jobportal_search_placeholder')}
          search={filter.search.q}
          setSearch={onSearch}
        />
        <JobFilters
          attributes={attributes}
          filter={filter}
          setFilter={updateFilters}
          jobs={jobs}
        />
        <div className="jobportal__results">
          {error ? (
            <p className="jobportal__message">{t('jobportal_error')}.</p>
          ) : (
            <>
              {filteredJobs?.length === 0 && !loading && (
                <h2 className="jobportal__noresult-header">{t('jobportal_noresults')}</h2>
              )}
              <JobportalResultsHeader
                numOfResults={filteredJobs?.length}
                maxEntries={maxEntries}
                setScreen={setScreen}
                loginUrl={loginUrl}
              />
            </>
          )}
          <JobList
            jobs={slicedJobs}
            loading={loading}
            newThresholdHours={newThresholdHours}
            setScreen={setScreen}
          />
          <LoadMore hasMore={showMoreResultsButton} onLoadMore={showMoreResults} />
        </div>
        <Topics
          apprenticeshipUrl={apprenticeshipUrl}
          careerUrl={careerUrl}
          volksschuleUrl={volksschuleUrl}
        />
      </PagesectionSubsection>
    </section>
  );
};

export default Jobportal;
